import { z } from 'zod';
import { macroCaptureTelemetrySchema, playbackAssetGrantSchema } from './macroExecution';
import { macroVisualDigestSchema, readGrantSchema } from './macroCalibration';
import { macroRegionSchema } from './macroTree';

/** Every presented frame within this many milliseconds of the anchor is captured. */
export const REFERENCE_CAPTURE_WINDOW_MS = 100;
/** Cap on frames kept from that window. Playjector copies at most one frame per 12 ms, so about 13-17. */
export const REFERENCE_CAPTURE_MAX_WINDOW_FRAMES = 16;
/**
 * Gaussian sigma for folding the window into one match score, in milliseconds.
 * Offset 0 weighs 1. A frame at ±50 ms weighs about 0.61. A frame at the ±100 ms edge weighs about 0.14.
 */
export const REFERENCE_MATCH_GAUSSIAN_SIGMA_MS = 50;
/** One live watch can score this many references across every checkpoint. Matches MaximumReferences. */
export const LIVE_MATCH_REFERENCE_BUDGET = 256;

/** Weight of a captured frame in the match score. The anchor (offset 0) weighs 1. */
export function referenceMatchWeight(offsetMs: number): number {
  const z = offsetMs / REFERENCE_MATCH_GAUSSIAN_SIGMA_MS;
  return Math.exp(-0.5 * z * z);
}
/**
 * How far before the click Playjector may place the anchor, in capture-clock milliseconds.
 * Live Detection sends this on every click. Authoring marks omit it and anchor on the click tick.
 */
export const REFERENCE_CAPTURE_MAX_LOOKBACK_MS = 250;
/** A ring sample counts for an offset when its capturedAt is within this of anchor+offset. */
export const REFERENCE_CAPTURE_OFFSET_TOLERANCE_MS = 25;
/** Minimum frames (anchor included) for a usable sample; matches localVisualTestSampleSchema.frames.min(3). */
export const REFERENCE_CAPTURE_MIN_FRAMES = 3;
/** How the GPU kernel folds 10-bit sources to 8 bits; every 16-bit PNG decoder must apply the same rule. */
export const REFERENCE_PIXEL_QUANTIZATION_RULE = 'rgb10-round-unorm-then-integer-(q*255+511)/1023-v1';

/** 16-bit PNG sample (0..65535, written as q<<6 | q>>4 from a 10-bit q) → 8-bit value. */
export function quantizeReference16BitSample(sample16: number): number {
  const q = sample16 >> 6;
  return Math.floor((q * 255 + 511) / 1023);
}

const boundedId = z.string().min(1).max(128);

export const referenceCaptureCommandSchema = z.discriminatedUnion('command', [
  z.object({
    command: z.literal('referenceCaptureBegin'),
    requestId: boundedId,
    sessionId: boundedId,
    assets: playbackAssetGrantSchema,
    outputPrefix: z.string().regex(/^reference\/[A-Za-z0-9_-]{1,64}\/$/),
  }).strict(),
  z.object({
    command: z.literal('referenceCaptureMark'),
    requestId: boundedId,
    sessionId: boundedId,
    markId: boundedId,
    lookbackMs: z.number().int().min(0).max(REFERENCE_CAPTURE_MAX_LOOKBACK_MS).optional(),
    region: macroRegionSchema.optional(),
  }).strict(),
  z.object({
    command: z.literal('referenceCaptureFocus'),
    requestId: boundedId,
    sessionId: boundedId,
    region: macroRegionSchema,
  }).strict(),
  z.object({
    command: z.literal('referenceCaptureEnd'),
    requestId: boundedId,
    sessionId: boundedId,
  }).strict(),
]);
export type ReferenceCaptureCommand = z.infer<typeof referenceCaptureCommandSchema>;

export const referenceCaptureErrorCodeSchema = z.enum([
  'invalid_command',
  'reference_capture_busy',
  'reference_capture_no_source',
  'reference_capture_session_unknown',
  'reference_capture_source_changed',
  'reference_capture_insufficient_frames',
  'reference_capture_format_unsupported',
  'reference_capture_source_too_large',
  'reference_capture_mark_limit',
  'reference_capture_encode_failed',
  'reference_capture_upload_failed',
  'reference_capture_cancelled',
  'reference_capture_timeout',
]);

export const referenceCaptureFrameSchema = z.object({
  id: boundedId,
  offsetMs: z.number().int().min(-REFERENCE_CAPTURE_WINDOW_MS).max(REFERENCE_CAPTURE_WINDOW_MS),
  capturedAtMs: z.number().int().nonnegative().safe(),
  frameSequence: z.number().int().nonnegative(),
  width: z.number().int().positive().max(16_384),
  height: z.number().int().positive().max(16_384),
  bitDepth: z.union([z.literal(8), z.literal(16)]),
  pixelDigest: macroVisualDigestSchema,
  asset: readGrantSchema,
}).strict();

const replyBase = {
  kind: z.literal('reference-capture'),
  requestId: boundedId,
  sessionId: boundedId,
};

const referenceCaptureSampleObjectSchema = z.object({
  ...replyBase,
  status: z.literal('sample'),
  markId: boundedId,
  anchorMs: z.number().int().nonnegative().safe(),
  anchorFrameSequence: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  capture: macroCaptureTelemetrySchema,
  frames: z.array(referenceCaptureFrameSchema).min(REFERENCE_CAPTURE_MIN_FRAMES).max(REFERENCE_CAPTURE_MAX_WINDOW_FRAMES),
}).strict();

// ZodEffects cannot be a discriminatedUnion member (it has no `.shape`). The sample
// checks specified on that member run here, only when status is `sample`.
export const referenceCaptureReplySchema = z.discriminatedUnion('status', [
  z.object({
    ...replyBase,
    status: z.literal('armed'),
    sourceId: z.string().max(128),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    capture: macroCaptureTelemetrySchema,
  }).strict(),
  z.object({
    ...replyBase,
    status: z.literal('marked'),
    markId: boundedId,
    anchorFrameSequence: z.number().int().nonnegative(),
    anchorMs: z.number().int().nonnegative().safe(),
  }).strict(),
  z.object({
    ...replyBase,
    status: z.literal('captured'),
    markId: boundedId,
    anchorFrameSequence: z.number().int().nonnegative(),
    anchorMs: z.number().int().nonnegative().safe(),
  }).strict(),
  referenceCaptureSampleObjectSchema,
  z.object({
    ...replyBase,
    status: z.literal('ended'),
    marksCompleted: z.number().int().nonnegative(),
  }).strict(),
  z.object({
    ...replyBase,
    status: z.literal('failed'),
    markId: boundedId.optional(),
    errorCode: referenceCaptureErrorCodeSchema,
  }).strict(),
]).superRefine((reply, context) => {
  if (reply.status !== 'sample') return;
  const anchors = reply.frames.filter((frame) => frame.offsetMs === 0);
  if (anchors.length !== 1) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['frames'],
      message: 'Sample requires exactly one offset-0 frame',
    });
  }
  const seenOffsets = new Set<number>();
  reply.frames.forEach((frame, index) => {
    if (seenOffsets.has(frame.offsetMs)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['frames', index, 'offsetMs'],
        message: 'Sample offsets must be unique',
      });
    }
    seenOffsets.add(frame.offsetMs);
    if (frame.width !== reply.width || frame.height !== reply.height) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['frames', index],
        message: 'Frame geometry must match the sample',
      });
    }
  });
});

export type ReferenceCaptureReply = z.infer<typeof referenceCaptureReplySchema>;
export type ReferenceCaptureSample = Extract<ReferenceCaptureReply, { status: 'sample' }>;
export type ReferenceCaptureFrame = z.infer<typeof referenceCaptureFrameSchema>;
